import { RefObject, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { useFocusTrap } from 'utils/hooks/useFocusTrap';
import noop from 'utils/noop';
import { MODAL_SIZES, MODAL_VARIANTS } from './Modal.constants';
import { ModalContainer, ModalOverlay, ScreenReaderText } from './Modal.styled';
import { ModalProvider } from './ModalContext';
import { DataSet, ModalProps } from './types';

/**
 * Aria tags for accessibility referenced from http://web-accessibility.carnegiemuseums.org/code/dialogs
 */
export const Modal = ({
  children,
  id,
  isModalOpen = false,
  withPortal = false,
  onClose = noop,
  size = MODAL_SIZES.MEDIUM,
  variant = MODAL_VARIANTS.DEFAULT,
  ...props
}: ModalProps) => {
  const modal = useRef<HTMLDialogElement>(null);
  useFocusTrap(onClose, modal);
  useClickAway(
    modal,
    (e) => {
      // other popups may exist, like for instance, a toast. without this check,
      // clicking on a toast would close the modal, which may not be an intended interaction.
      // so we only close the modal, if a user clicks on the overlay.
      if (e.target instanceof HTMLElement && e.target.id === DataSet.ModalOverlayId) {
        onClose();
      }
    },
    ['mousedown'],
  );

  const [bodyOverflow, setBodyOverflow] = useState('unset');

  /**
   * Disables background scrolling on-mount
   */

  useEffect(() => {
    setBodyOverflow(document.body.style.overflow);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = bodyOverflow;
    };
  }, []);

  return (
    <ModalProvider withPortal={withPortal} size={size} variant={variant} onClose={onClose}>
      <ModalOverlay id={DataSet.ModalOverlayId} data-test={DataSet.ModalOverlayTestId} {...props}>
        <ModalContainer
          aria-labelledby="header"
          aria-describedby="modal-description"
          aria-hidden={!isModalOpen}
          aria-modal
          data-test={`modal-${id}`}
          id={id}
          ref={modal as RefObject<HTMLDialogElement>}
          role="dialog"
          size={size}
          onClick={(event) => event.stopPropagation()}
        >
          <ScreenReaderText id="modal-description">
            Beginning of modal window. It begins with a header. Escape will cancel and close the
            window.
          </ScreenReaderText>
          {children}
        </ModalContainer>
      </ModalOverlay>
    </ModalProvider>
  );
};
